/**
 * 소셜 로그인 모듈 — 네이버/카카오 인증 통합
 *
 * 진입점:
 *   $j.F.Social.login('naver' | 'kakao')
 *
 * 모든 외부 입력은 EventBus 'social:complete' 로 수렴 → 내부 _end() 가 next 분기.
 *
 * 인증 흐름:
 *   1) 앱 환경: $j.F.App.runSocial(provider)
 *      → 앱 native 인증 → callbackBroker → EventBus 'app:social:result' (raw token)
 *      → _initBus 구독자가 /m/ajax/{provider}/callback/login POST
 *      → 응답을 EventBus 'social:complete' 로 trigger
 *
 *   2) 웹 환경(앱 없음): popup window 로 /m/plugin/{provider}/auth
 *      → 서버가 Socialite 인증 + 라우팅 후 callback.blade.php 가 최종 결과를
 *        window.opener.postMessage({type:'jb:social:complete', payload}) 로 전달
 *      → _initBus 의 message 리스너가 EventBus 'social:complete' 로 trigger
 *
 *   수렴: 'social:complete' 구독자가 _end(data) 호출 → next(L/J/C) 분기.
 */
import Store from '../Common/Store';
import Util from '../Common/Util';

export default class Social {
    constructor() {
        this._popupWidth = 400;
        this._popupHeight = 620;
        // dev 진단 모드 — true 일 때 EventBus 구독자가 후속 로직(ajax/postMessage/_end)을 모두 건너뜀.
        // DevBridge 페이지가 initialize 에서 켜고, native 반환값만 콘솔/패널에서 확인할 수 있게 해준다.
        this.suppressed = false;
        this._initBus();
    }

    /**
     * 소셜 로그인 진입점
     * @param {'naver'|'kakao'} provider
     */
    login = (provider) => {
        // 자동로그인 여부를 쿠키에 박아두자. 소셜인증은 여러단계를 거치기 때문에 이값을 기억하기위해
        // 화면마다 칸이 다르다 — 옛 로그인(login)은 체크박스, 장비불러(loginV2)는 토글이 값을 바꾸는 hidden('1'|'').
        // hidden 은 :checked 가 될 수 없어 체크박스로만 읽으면 소셜 로그인이 늘 '자동로그인 안 함'이 된다.
        const $remember = $('input[name="remember"]');
        const remember = $remember.is(':checkbox') ? $remember.is(':checked') : $remember.val() === '1';
        Store.cookie.set(G5['CK_REMEMBER'], remember ? 1 : 0); // PHP: $_COOKIE[G5_COOKIE_REMEMBER]

        setTimeout(() => {
            if (!$j.F.App.runSocial(provider)) {
                this._start(provider);
            }
        }, 100);
    }

    /**
     * 소셜인증 결과 분기 — 내부 전용. 'social:complete' 구독자만 호출.
     * 외부 모듈/popup 에서 직접 호출 금지 (EventBus 경유로 통일).
     *
     * @param {Object} data
     * @param {boolean} data.result  - 인증 결과
     * @param {string}  data.message - 실패 메시지
     * @param {'L'|'J'|'C'} data.next - 다음 동작 (L=로그인, J=가입, C=연결)
     * @private
     */
    _end = (data) => {
        if (!data.result) {
            Util.swalert(data.message);
            return;
        }
        switch (data.next) {
            case 'L': // 소셜인증 후 로그인
                location.href = '/m';
                break;
            case 'J': // 소셜최초인증 후 본인인증 진행
                location.href = '/m/signup/agreement?type=S';
                break;
            case 'C': // 소셜연결하기
                Util.swalert('소셜계정 연결이 완료되었습니다.', () => location.reload());
                break;
        }
    }

    /**
     * popup window 로 web 인증 시작 (앱 미지원 폴백)
     * @private
     */
    _start = (provider) => {
        const win = this._popupCenter();
        if (!win) return;
        win.location.href = `/m/plugin/${provider}/auth`;

        const timer = setInterval(() => {
            if (win.closed) {
                clearInterval(timer);
                $j.Common.Util.loadingOverlay(false);
            }
        }, 500);
    }

    /** @private */
    _popupCenter = () => {
        let xPos = (document.body.offsetWidth / 2) - (this._popupWidth / 2);
        xPos += window.screenLeft; // 듀얼 모니터일 때
        return window.open(
            '',
            'login_popup',
            `width=${this._popupWidth}, height=${this._popupHeight}, left=${xPos}, menubar=yes, status=yes, titlebar=yes, resizable=yes`
        );
    }

    /**
     * 외부 입력 → 'social:complete' 수렴 → _end() 호출.
     * 싱글톤이라 페이지 무관 한 번만 등록.
     * @private
     */
    _initBus = () => {
        // [앱 경로] raw token 수신 → 서버 ajax → 'social:complete' 발화
        $j.Common.EventBus.on('$j.F.Social', 'app:social:result', (e, data) => {
            if (this.suppressed) {
                console.log('[Social] app:social:result suppressed (dev mode)', data);
                $j.Common.Util.loadingOverlay(false);
                return;
            }
            const res      = data && data.res;
            const token    = data && data.token;
            const sns_type = data && data.sns_type;
            console.log(`[app:social:result] res=${res} token=${token} sns_type=${sns_type}`);

            let provider;
            if (sns_type === 'N')      provider = 'naver';
            else if (sns_type === 'K') provider = 'kakao';
            else {
                Util.swalert('지원하지 않는 소셜 타입입니다.');
                $j.Common.Util.loadingOverlay(false);
                return;
            }

            // native 인증 실패 — 카카오/네이버 모두 popup OAuth 로 폴백.
            // 네이버: 앱 측 NaverLoginHandler 가 네이버앱 미설치 시 즉시 실패. 카카오: SDK 자체 폴백 후 추가 실패한 경우.
            if (!res) {
                $j.Common.Util.loadingOverlay(false);
                this._start(provider);
                return;
            }

            $.ajax({
                url:  `/m/ajax/${provider}/callback/login`,
                type: 'POST',
                data: { res, token, sns_type },
                success: (response) => {
                    $j.Common.Util.loadingOverlay(false);
                    $j.Common.EventBus.trigger('social:complete', response);
                },
            });
        });

        // [popup 경로] callback.blade.php 의 postMessage 수신 → 'social:complete' 발화
        window.addEventListener('message', (e) => {
            if (e.origin !== location.origin) return;
            const msg = e.data;
            if (!msg || msg.type !== 'jb:social:complete') return;
            if (this.suppressed) {
                console.log('[Social] postMessage suppressed (dev mode)', msg.payload);
                return;
            }
            $j.Common.EventBus.trigger('social:complete', msg.payload);
        });

        // [수렴점] 양쪽 경로의 최종 결과를 _end 로 디스패치
        $j.Common.EventBus.on('$j.F.Social', 'social:complete', (e, data) => {
            if (this.suppressed) {
                console.log('[Social] social:complete suppressed (dev mode)', data);
                return;
            }
            this._end(data);
        });
    }
}
